'use client';

import React from 'react';

import type { TDynamicData } from '../../types/dynamic-data';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';

import { MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export function Technologies({
  technologies,
  subtitle,
  title,
}: {
  technologies: TDynamicData[];
  subtitle: string;
  title: string;
}) {
  const [activeTab, setActiveTab] = React.useState(technologies[0]?.name || '');

  const activeTech = technologies.find(tech => tech.name === activeTab);
  const activeTechFields = activeTech?.fields ?? [];

  return (
    <section
      className={cn(
        'custom-section',
        'gap-8 bg-[#FAFAFA] pb-8 pr-0 pt-16 sm:gap-10 sm:pb-12 sm:pt-20 md:pb-16 md:pt-28 lg:gap-12 lg:pb-[72px] lg:pt-32 xl:pt-36',
      )}
    >
      <Heading
        subtitle={subtitle}
        title={title}
        className={{
          container: 'items-center justify-center text-center',
          title: 'ea-primary-heading',
        }}
      />
      <Tabs
        value={activeTab}
        defaultValue={activeTab}
        onValueChange={value => setActiveTab(value)}
        className={cn(
          'flex w-full flex-col items-start justify-start gap-8 sm:gap-10 md:flex-row md:justify-between lg:gap-12',
          'custom-container',
        )}
      >
        <TabsList className="hide-scroll-bar flex h-fit w-full flex-nowrap items-start justify-start gap-4 overflow-x-scroll rounded-none bg-transparent shadow-none md:flex-col md:gap-0 md:overflow-x-visible">
          {technologies.map(tech => (
            <TabsTrigger
              key={tech.slug}
              value={tech.name}
              className={cn(
                'flex items-center justify-between rounded-none border-b px-0 py-4 text-left text-base font-bold tracking-tighter sm:text-lg md:w-full md:max-w-[486px] lg:text-xl xl:text-2xl',
                'ea-technologies__tabs-trigger',
              )}
            >
              {tech.name}
              <MdOutlineKeyboardArrowRight
                size={24}
                className="invisible hidden text-primary md:block"
              />
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent
          value={activeTab}
          className="flex w-full flex-col items-start justify-start gap-8 pr-4 md:max-w-[642px]"
        >
          <h3 data-ea-visual-tag="h4" className="text-xl font-bold tracking-tighter sm:text-2xl lg:text-3xl">
            {activeTech?.name}
          </h3>
          <HtmlRendererRaw
            className={cn('w-full text-muted-foreground', 'ea-technologies__description')}
            html={activeTechFields[0]?.value || ''}
          />
        </TabsContent>
      </Tabs>
    </section>
  );
}
